import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { SEO_CONFIG } from './config/seoConfig';

const SeoManager: React.FC = () => {
  const { i18n } = useTranslation();
  const location = useLocation();
  const part_number = location.pathname.split('/').pop() || '';
  const [currentLang, setCurrentLang] = useState<'zh' | 'en'>('zh');
  
  useEffect(() => {
    if (i18n.language !== 'zh') {
      i18n.changeLanguage('zh');
    }

    const handleLanguageChanged = (lng: string) => {
      setCurrentLang(lng as 'zh' | 'en');
    };

    i18n.on('languageChanged', handleLanguageChanged);
    return () => {
      i18n.off('languageChanged', handleLanguageChanged);
    };
  }, [i18n]);

  const basePath = location.pathname.replace(/\/[^/]+$/, '');
  const defaultSeoData = {
    title: { 
      zh: '研达创新EETL | 电子元器件定制与批量采购| TAIYO HRS授权代理商', 
      en: 'EETL Electronics - Electronic Components Supplier' 
    },
    description: { 
      zh: '研达创新EETL是国内领先的电子元器件供应链解决方案提供商，公司致力成为优秀的电子元器件代理及增值服务提供商。代理产品线包括TAIYO YUDEN、ELNA、HRS、PROSEMI、ROHM、SUNWAY、INVSEMI等知名品牌，提供电容器、电感器、连接器等高品质元器件及一站式服务。覆盖消费电子、通信、汽车电子、智能驾驶、新能源动力、工业控制、新能源储能及AI服务器、AI机器人行业，依托香港、深圳、上海三地物流及多元化业务平台中心，确保高效、可靠的供应链支持。', 
      en: 'Default Description' 
    },
    keywords: { 
      zh: ['工业级电子元器件', '汽车级电子元器件', '电子元器件代理商', '高性能电容器', '工业级电感器', '定制电子元器件解决方案', 'TAIYO/HRS授权代理商'], 
      en: ['Default Keywords'] 
    }
  };

  const isProductPage = location.pathname.startsWith('/product/');
  let seoData = SEO_CONFIG[basePath] || SEO_CONFIG[location.pathname] || defaultSeoData;

  if (part_number && isProductPage) {
    seoData = {
      title: { 
        zh: `${part_number} ${seoData.title.zh.split('|')[0].trim()} - 研达创新EETL`,
        en: `${seoData.title.en} - ${part_number}`
      },
      description: {
        zh: `${seoData.description.zh}（型号：${part_number}）`,
        en: `${seoData.description.en} (Model: ${part_number})`
      },
      keywords: {
        zh: [...seoData.keywords.zh, part_number],
        en: [...seoData.keywords.en, part_number]
      }
    };
  }

  const title = seoData.title[currentLang] || seoData.title.zh;
  const description = seoData.description?.[currentLang] || seoData.description.zh || '';
  const keywords = (seoData.keywords?.[currentLang] || seoData.keywords.zh || []).join(', ');


  return (
    <Helmet>
      <title>{title}</title>
      <meta name="description" content={description} />
      <meta name="keywords" content={keywords} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
    </Helmet>
  );
};

export default SeoManager;